import React, { useEffect, useState, useRef } from "react";
import { Search, ChevronDown, Activity, BarChart3 } from "lucide-react";
import { motion } from "framer-motion";
import { DatePicker, ConfigProvider, Spin,theme as antTheme } from "antd";
import { SwapOutlined } from "@ant-design/icons";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Bar,
  BarChart,
  CartesianGrid,
  Tooltip,
  Legend,
  Label,
  ResponsiveContainer,
} from "recharts";
import { useDistrict } from "../utils/DistrictContext";
import { toast } from "react-toastify";
import moment from "moment";
import { getMetersByFlowList, getMetersByFlowChart } from "../services/index";
import { useTheme } from "../context/ThemeContext";
import dayjs from "dayjs";
const { RangePicker } = DatePicker;
const colorPalette = ["#ff6b6b", "#51cf66", "#4c6ef5", "#ffcc00"];

const MetersByFlowTab = () => {
  const [meterSearchQuery, setMeterSearchQuery] = useState("");
  const [headings, setHeadings] = useState([]);
  const [selectedMeters, setSelectedMeters] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sideMenuLoader, setSideMenuLoader] = useState(false);
  const [chartType, setChartType] = useState("line");

  // State to track which categories are collapsed (array of category codes)
  const [collapsedCategories, setCollapsedCategories] = useState([]);

  // Filters
  const [rangeOption, setRangeOption] = useState("Past Month");
  const [intervalOption, setIntervalOption] = useState("Week");
  const [intervalValue, setIntervalValue] = useState(1);

  // Custom Date Range States
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const { theme, colors } = useTheme();

  const { selectedDistrict } = useDistrict();
  const districtRef = useRef(selectedDistrict);
  const [userId, setUserId] = useState(null);
  const requestIdRef = useRef(0);

  // --- Initial Setup ---
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("MCData"));
    if (user?.userid) setUserId(user.userid);
    setSelectedMeters([]);
    setChartData([]);
  }, [selectedDistrict]);

  // Expand all categories automatically when the user is searching
  // useEffect(() => {
  //   if (meterSearchQuery.trim() !== "") {
  //     setCollapsedCategories([]);
  //   }
  // }, [meterSearchQuery]);
  useEffect(() => {
    if (meterSearchQuery.trim() !== "") {
      setCollapsedCategories([]);
    } else {
      setCollapsedCategories(headings.map((h) => h.code));
    }
  }, [meterSearchQuery, headings]);

  // --- API Call: Fetch List ---
  useEffect(() => {
    const fetchList = async () => {
      if (!userId) return;
      if (rangeOption === "Custom" && (!startDate || !endDate)) return;

      setSideMenuLoader(true);
      try {
        const res = await getMetersByFlowList(
          intervalOption,
          rangeOption,
          userId,
          startDate,
          endDate,
        );
        if (res?.data?.water_flow) {
          const mapped = Object.keys(res.data.water_flow).map((code) => ({
            code,
            title: res.data.water_flow[code].category_name,
            meters: res.data.water_flow[code].water_data,
          }));
          setHeadings(mapped);
          setCollapsedCategories(mapped.map((h) => h.code));
        }
      } catch (error) {
        console.error("Failed to load meter list");
      } finally {
        setSideMenuLoader(false);
      }
    };
    fetchList();
  }, [
    rangeOption,
    intervalOption,
    userId,
    selectedDistrict,
    startDate,
    endDate,
  ]);

  // --- API Call: Fetch Chart Data ---
useEffect(() => {
  const fetchData = async () => {
    if (selectedMeters.length === 0) {
      setChartData([]);
      return;
    }

    if (rangeOption === "Custom" && (!startDate || !endDate)) return;

    const currentRequestId = ++requestIdRef.current;

    setLoading(true);

    try {
      const res = await getMetersByFlowChart(
        intervalOption,
        rangeOption,
        intervalValue,
        selectedMeters,
        startDate,
        endDate
      );

      // 🔴 Ignore outdated responses
      if (currentRequestId !== requestIdRef.current) return;

      if (res?.data?.water_flow) {
        const formatted = res.data.water_flow.map((item) => {
          const entry = { date: item[0].flow_date_check };

          item.forEach((m) => {
            const key = `${m.meter},${m.register_number?.trim() || ""}`;
            entry[key] = m.flow;
          });

          return entry;
        });

        setChartData(formatted);
      }
    } catch (error) {
      console.error("Chart data error");
    } finally {
      // 🔴 Only latest request controls loader
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  fetchData();
}, [
  selectedMeters,
  rangeOption,
  intervalOption,
  intervalValue,
  startDate,
  endDate,
]);

  // --- Handlers ---
  const toggleMeterSelection = (categoryCode, meterId, regNum) => {
    const meterKey = `${meterId},${regNum?.trim() || ""}`;

    setSelectedMeters((prev) => {
      const totalCount = prev.reduce((sum, cat) => sum + cat.meters.length, 0);
      const catIndex = prev.findIndex((c) => c.code === categoryCode);
      const isAlreadySelected =
        catIndex > -1 && prev[catIndex].meters.includes(meterKey);

      if (!isAlreadySelected && totalCount >= 4) {
        toast.warning("You can select only 4 meters at a time");
        return prev;
      }

      if (catIndex > -1) {
        const newMeters = isAlreadySelected
          ? prev[catIndex].meters.filter((m) => m !== meterKey)
          : [...prev[catIndex].meters, meterKey];

        if (newMeters.length === 0)
          return prev.filter((c) => c.code !== categoryCode);
        const updated = [...prev];
        updated[catIndex] = { ...updated[catIndex], meters: newMeters };
        return updated;
      } else {
        return [...prev, { code: categoryCode, meters: [meterKey] }];
      }
    });
  };

  // const isMeterSelected = (meterId) => {
  //   return selectedMeters.some((cat) =>
  //     cat.meters.some((m) => m.split(",")[0] === meterId),
  //   );
  // };
  const isMeterSelected = (meterId, regNum) => {
    const targetKey = `${meterId},${regNum?.trim() || ""}`;
    return selectedMeters.some((cat) => cat.meters.includes(targetKey));
  };
  const toggleCategoryCollapse = (categoryCode) => {
    setCollapsedCategories((prev) =>
      prev.includes(categoryCode)
        ? prev.filter((code) => code !== categoryCode)
        : [...prev, categoryCode],
    );
  };
  const disabledDate = (current) => {
    // Can not select days after today
    return current && current > moment().endOf("day");
  };
  const handleDateChange = (dates, dateStrings) => {
    if (dateStrings && dateStrings[0] !== "" && dateStrings[1] !== "") {
      setStartDate(dateStrings[0]);
      setEndDate(dateStrings[1]);
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  };

  // --- Search Filtering ---
  const query = meterSearchQuery.toLowerCase().trim();

  const filteredHeadings = headings
    .map((h) => ({
      ...h,
      meters: h.meters.filter((m) => {
        if (!query) return true;

        const meterId = String(m.meter_id || "").toLowerCase();
        const overrideName = String(m.overided_name || "").toLowerCase();
        const registerNum = String(m.register_number || "").toLowerCase();

        return (
          meterId.includes(query) ||
          overrideName.includes(query) ||
          registerNum.includes(query)
        );
      }),
    }))
    .filter((h) => h.meters.length > 0);

  return (
    <div className="flex gap-6 p-4 flex-wrap">
      {/* Left Sidebar */}
      <motion.div
        className="w-80 backdrop-blur-sm rounded-xl border p-6 max-h-[800px] min-[1921px]:max-h-[1200px] overflow-y-auto max-[1441px]:w-full"
        style={{ backgroundColor: colors.cardBg, borderColor: colors.border }}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
      >
        <h3 className=" font-semibold mb-4" style={{ color: colors.text }}>
          Meters
        </h3>
        <div className="relative mb-4">
          <input
            type="text"
            placeholder="Search Meters"
            value={meterSearchQuery}
            onChange={(e) => setMeterSearchQuery(e.target.value)}
            className="w-full rounded-lg pl-10 pr-4 py-2.5 text-sm outline-none"
            style={{
              background: colors.background,
              border: `1px solid ${colors.border}`,
              color: colors.text,
            }}
          />
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
            style={{ color: colors.textSecondary }}
          />
        </div>

        {sideMenuLoader ? (
          <div className="text-white text-center py-10"> <Spin size="large" />
            <span className="mt-2 block" style={{color: colors.textSecondary}}> Fetching the data...</span>
          </div>
        ) : (
          filteredHeadings.map((category) => {
            const isCollapsed = collapsedCategories.includes(category.code);

            return (
              <div key={category.code} className="mb-4">
                {/* Category Header (Clickable for Collapse) */}
                <div
                  className="flex items-center justify-between mb-2 cursor-pointer group"
                  onClick={() => toggleCategoryCollapse(category.code)}
                >
                  <div className="flex items-center gap-2">
                    <ChevronDown
                      className={`w-4 h-4 transition-transform duration-200 ${
                        isCollapsed ? "-rotate-90" : "rotate-0"
                      }`}
                      style={{ color: colors.textSecondary }}
                    />
                    <h4
                      style={{
                        color: colors.text,
                      }}
                      className="font-medium text-sm group-hover:text-[#00b8f1] transition-colors"
                    >
                      {category.title}
                    </h4>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent collapse when clicking Clear
                      setSelectedMeters((prev) =>
                        prev.filter((p) => p.code !== category.code),
                      );
                    }}
                    className="text-[#00b8f1] text-xs hover:underline px-2 py-1"
                  >
                    Clear
                  </button>
                </div>

                {/* Meter List (Rendered only if NOT collapsed) */}
                {!isCollapsed && (
                  <div className="space-y-2 mt-3">
                    {category.meters.map((meter, index) => {
                      // const selected = isMeterSelected(meter.meter_id);
                      const selected = isMeterSelected(
                        meter.meter_id,
                        meter.register_number,
                      );
                      const reactKey = `${category.code}-${meter.meter_id}-${index}`;
                      return (
                        <div
                          key={reactKey}
                          onClick={() =>
                            toggleMeterSelection(
                              category.code,
                              meter.meter_id,
                              meter.register_number,
                            )
                          }
                          className="flex items-start gap-3 p-3 rounded-lg border transition-all cursor-pointer"
                          style={{
                            background: selected
                              ? `${colors.primary}20`
                              : `${colors.background}80`,
                            borderColor: selected
                              ? colors.primary
                              : colors.border,
                            boxShadow: selected
                              ? `0 0 20px ${colors.primary}30`
                              : "none",
                          }}
                        >
                          <div
                            className="w-5 h-5 rounded border flex items-center justify-center shrink-0 mt-0.5"
                            style={{
                              backgroundColor: selected
                                ? colors.primary
                                : "transparent",
                              border: `1px solid ${
                                selected ? colors.primary : colors.border
                              }`,
                            }}
                          >
                            {selected && (
                              <div className="w-2 h-2 bg-white rounded-full" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div
                              className="text-sm font-medium  truncate"
                              style={{ color: colors.text }}
                            >
                              {meter.overided_name || meter.meter_id}
                            </div>
                            <div
                              className="text-xs"
                              style={{ color: colors.textSecondary }}
                            >
                              {meter.meter_id}{" "}
                              {meter.register_number
                                ? ` (${meter.register_number})`
                                : ""}
                            </div>
                            <div
                              className="text-sm mt-1"
                              style={{ color: colors.textSecondary }}
                            >
                              Flow: {Number(meter.flow).toLocaleString()}{" "}
                              Gallons
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </motion.div>

      {/* Main Content */}
      <div className="flex-1 space-y-6 max-[992px]:overflow-hidden">
        {/* Replace the Filter motion.div in your return block with this: */}
        <motion.div
          className="backdrop-blur-sm rounded-xl border p-6"
          style={{ backgroundColor: colors.cardBg, borderColor: colors.border }}
        >
          <div className="flex flex-wrap items-end gap-4 justify-end">
            <div className="">
              <label
                className="text-xs mb-2 block"
                style={{ color: colors.primary }}
              >
                Range | Custom | Interval
              </label>
              <div
                className="flex items-center gap-1 p-1 rounded-xl border w-full justify-between flex-wrap"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.border,
                }}
              >
                {/* --- SECTION 1: RANGE PRESETS --- */}
                <div className="flex items-center gap-1 flex-wrap">
                  {[
                    { label: "D", value: "Past Day" },
                    { label: "W", value: "Past Week" },
                    { label: "1M", value: "Past Month" },
                    { label: "3M", value: "Past 3 Months" },
                    { label: "YTD", value: "YTD" },
                    { label: "1Y", value: "Past Year" },
                  ].map((tp) => {
                    // Logic to highlight YTD even if state is technically "Custom"
                    const isYTD =
                      tp.value === "YTD" &&
                      startDate ===
                        moment().startOf("year").format("YYYY-MM-DD");
                    const isActive = rangeOption === tp.value || isYTD;

                    return (
                      <button
                        key={tp.value}
                        onClick={() => {
                          if (tp.value === "YTD") {
                            const startOfYear = moment()
                              .startOf("year")
                              .format("YYYY-MM-DD");
                            const today = moment().format("YYYY-MM-DD");
                            setStartDate(startOfYear);
                            setEndDate(today);
                            setRangeOption("Custom"); // Trick API to use dates
                            setIntervalOption("Week");
                          } else {
                            setRangeOption(tp.value);
                            setStartDate(null);
                            setEndDate(null);
                          }
                        }}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer hover:opacity-80"
                        style={{
                          backgroundColor: isActive
                            ? `${colors.primary}20`
                            : "transparent",
                          color: isActive
                            ? colors.primary
                            : colors.textSecondary,
                          border: isActive
                            ? `1px solid ${colors.primary}40`
                            : "1px solid transparent",
                        }}
                      >
                        {tp.label}
                      </button>
                    );
                  })}
                </div>
                {/* Vertical Separator 2 */}
                <div
                  className="h-6 w-[1px] mx-1"
                  style={{ backgroundColor: colors.border }}
                />

                {/* --- SECTION 3: CALENDAR --- */}
                <ConfigProvider
                  theme={{
                    algorithm:
                      theme === "white"
                        ? antTheme.defaultAlgorithm
                        : antTheme.darkAlgorithm,
                    token: {
                      colorPrimary: colors.primary,
                      controlHeight: 32,
                      borderRadius: 8,
                    },
                  }}
                >
                  <RangePicker
                    value={[
                      startDate ? dayjs(startDate, "YYYY-MM-DD") : null,
                      endDate ? dayjs(endDate, "YYYY-MM-DD") : null,
                    ]}
                    onChange={(dates, strings) => {
                      handleDateChange(dates, strings);
                      setRangeOption("Custom");
                    }}
                    disabledDate={disabledDate}
                    placeholder={["Start", "End"]}
                    variant="borderless"
                    separator={
                      <SwapOutlined style={{ color: colors.textSecondary }} />
                    }
                    className="custom-range-picker cursor-pointer"
                    style={{ width: "240px" }}
                    classNames={{
                          popup: {
                            root: `mizu-calendar-popup calendar-${theme}`,
                          },
                        }}
                  />
                </ConfigProvider>
                {/* Vertical Separator 1 */}
                <div
                  className="h-6 w-[1px] mx-1"
                  style={{ backgroundColor: colors.border }}
                />

                {/* --- SECTION 2: INTERVAL SELECTOR --- */}
                <div className="flex items-center gap-1">
                  {["Hour", "Day", "Week"].map((intvl) => {
                    const isActive = intervalOption === intvl;

                    // We removed the Range check for Hour so it always shows
                    // We only keep the check to hide Week for very short ranges if desired
                    if (intvl === "Week" && ["Past Day"].includes(rangeOption))
                      return null;

                    return (
                      <button
                        key={intvl}
                        onClick={() => setIntervalOption(intvl)}
                        className="px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all cursor-pointer hover:opacity-80"
                        style={{
                          backgroundColor: isActive
                            ? colors.primary
                            : "transparent",
                          color: isActive ? "#fff" : colors.textSecondary,
                        }}
                      >
                        {intvl}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        <div className="w-full max-[767px]:overflow-x-auto">
          {/* <motion.div
            className="rounded-xl border p-6 h-[500px] min-[1921px]:h-[900px] max-[992px]:w-[800px]"
            style={{
              backgroundColor: colors.cardBg,
              borderColor: colors.border,
            }}
          >
            {loading ? (
              <div className="h-full flex items-center justify-center text-white">
                Loading ...
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                  <XAxis
                    dataKey="date"
                    stroke={colors.textSecondary}
                    tickFormatter={(value) => {
                      return moment(value, "dddd, MMM DD, YYYY hh:mm A").format(
                        "MMM DD, YYYY",
                      );
                    }}
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis
                    stroke={colors.textSecondary}
                    tickFormatter={(v) => v.toLocaleString()}
                    width={120}
                  >
                    <Label
                      value="Gallons"
                      angle={-90}
                      position="insideLeft"
                      
                      style={{
                        fill: colors.textSecondary,
                        fontSize: "14px",
                        textAnchor: "middle",
                      }}
                      offset={1}
                    />
                  </YAxis>
                  <Tooltip
                    
                    contentStyle={{
                      backgroundColor:
                        theme === "white" ? "#ffffff" : colors.background,
                      border: `1px solid ${colors.primary}30`,
                      borderRadius: "8px",
                      boxShadow: `0 8px 32px ${colors.primary}30`,
                      color: colors.text,
                    }}
                    labelStyle={{
                      color: colors.text,
                      fontWeight: "bold",
                      marginBottom: "4px",
                    }}
                    formatter={(value, name) => {
                       const cleanName = name.split(",")[0];
                      return [value.toLocaleString(), cleanName];
                    }}
                  />
                  <Legend />
                 
                  {selectedMeters
                    .flatMap((c) => c.meters)
                    .map((mKey, idx) => {
                     
                      return (
                        <Line
                          key={mKey}
                          type="monotone"
                          dataKey={mKey} // Use the unique key here
                          stroke={colorPalette[idx % colorPalette.length]}
                          strokeWidth={2}
                          dot={{ r: 4 }}
                        />
                      );
                    })}
                </LineChart>
              </ResponsiveContainer>
            )}
          </motion.div> */}
          <motion.div
            className="rounded-xl border p-6 h-[500px] min-[1921px]:h-[900px] max-[992px]:w-[800px] relative"
            style={{
              backgroundColor: colors.cardBg,
              borderColor: colors.border,
            }}
          >
            {/* Chart Type Toggle Icon */}
            <div className="absolute top-4 right-6 z-10 flex gap-2">
              <button
                onClick={() =>
                  setChartType(chartType === "line" ? "bar" : "line")
                }
                className="flex items-center justify-center w-9 h-9 rounded-lg border transition-all hover:bg-white/10"
                style={{ borderColor: colors.border, color: colors.primary }}
                title={
                  chartType === "line"
                    ? "Switch to Bar Chart"
                    : "Switch to Line Chart"
                }
              >
                {chartType === "line" ? (
                  <BarChart3 size={18} />
                ) : (
                  <Activity size={18} />
                )}
              </button>
            </div>

            {loading ? (
              <div className="h-full flex items-center justify-center text-white">
                 <Spin size="large" />
                   <span className="ml-2 block" style={{color: colors.textSecondary}}> Fetching the data...</span>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                {chartType === "line" ? (
                  <LineChart data={chartData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={colors.border}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      stroke={colors.textSecondary}
                      tickFormatter={(value) =>
                        moment(value).format("MMM DD, YYYY")
                      }
                      tick={{ fontSize: 10 }}
                    />
                    <YAxis
                      stroke={colors.textSecondary}
                      tickFormatter={(v) => v.toLocaleString()}
                      width={120}
                    >
                      <Label
                        value="Gallons"
                        angle={-90}
                        position="insideLeft"
                        style={{
                          fill: colors.textSecondary,
                          fontSize: "14px",
                          textAnchor: "middle",
                        }}
                        offset={1}
                      />
                    </YAxis>
                    <Tooltip
                      contentStyle={{
                        backgroundColor:
                          theme === "white" ? "#ffffff" : colors.background,
                        border: `1px solid ${colors.primary}30`,
                        borderRadius: "8px",
                        color: colors.text,
                      }}
                      formatter={(value, name) => [
                        value.toLocaleString(),
                        name.split(",")[0],
                      ]}
                    />
                    <Legend />
                    {selectedMeters
                      .flatMap((c) => c.meters)
                      .map((mKey, idx) => (
                        <Line
                          key={mKey}
                          type="monotone"
                          dataKey={mKey}
                          stroke={colorPalette[idx % colorPalette.length]}
                          strokeWidth={2}
                          dot={{ r: 4 }}
                        />
                      ))}
                  </LineChart>
                ) : (
                  <BarChart data={chartData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={colors.border}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      stroke={colors.textSecondary}
                      tickFormatter={(value) =>
                        moment(value).format("MMM DD, YYYY")
                      }
                      tick={{ fontSize: 10 }}
                    />
                    <YAxis
                      stroke={colors.textSecondary}
                      tickFormatter={(v) => v.toLocaleString()}
                      width={120}
                    >
                      <Label
                        value="Gallons"
                        angle={-90}
                        position="insideLeft"
                        style={{
                          fill: colors.textSecondary,
                          fontSize: "14px",
                          textAnchor: "middle",
                        }}
                        offset={1}
                      />
                    </YAxis>
                    <Tooltip
                      cursor={{ fill: "rgba(255,255,255,0.05)" }}
                      contentStyle={{
                        backgroundColor:
                          theme === "white" ? "#ffffff" : colors.background,
                        border: `1px solid ${colors.primary}30`,
                        borderRadius: "8px",
                        color: colors.text,
                      }}
                      formatter={(value, name) => [
                        value.toLocaleString(),
                        name.split(",")[0],
                      ]}
                    />
                    <Legend />
                    {selectedMeters
                      .flatMap((c) => c.meters)
                      .map((mKey, idx) => (
                        <Bar
                          key={mKey}
                          dataKey={mKey}
                          fill={colorPalette[idx % colorPalette.length]}
                          name={mKey.split(",")[0]}
                          radius={[4, 4, 0, 0]}
                        />
                      ))}
                  </BarChart>
                )}
              </ResponsiveContainer>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default MetersByFlowTab;
